import React, { useState } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Box from '@amzn/meridian/box';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Toggle from '@amzn/meridian/toggle';
import Icon from '@amzn/meridian/icon';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Text from '@amzn/meridian/text';
import { WorkstationState } from 'src/pages/settings/tabs/siteLayout/types';
import { useAppDispatch } from 'src/store';
import {
  addWorkstation,
  removeWorkstation,
  toggleWorkstation,
  renameWorkstation,
} from 'src/store/slices/siteLayoutSlice';

export interface WorkstationChipListProps {
  workstations: WorkstationState[];
  processPathIndex: number;
  lineIndex?: number;
}

const WorkstationChipList: React.FC<WorkstationChipListProps> = ({
  workstations,
  processPathIndex,
  lineIndex,
}) => {
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);

  const handleAdd = () => {
    if (inputValue.trim()) {
      dispatch(addWorkstation({ processPathIndex, lineIndex, workstationId: inputValue.trim() }));
      setInputValue('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAdd();
    }
  };

  const handleToggle = (index: number, checked: boolean) => {
    dispatch(toggleWorkstation({ processPathIndex, lineIndex, workstationIndex: index, enabled: checked }));
  };

  const handleRename = (index: number, newId: string) => {
    dispatch(renameWorkstation({ processPathIndex, lineIndex, workstationIndex: index, workstationId: newId }));
  };

  const handleRemove = (index: number) => {
    dispatch(removeWorkstation({ processPathIndex, lineIndex, workstationIndex: index }));
  };

  return (
    <Column spacing="300">
      {workstations.length > 0 && (
        <Column spacing="200">
          {workstations.map((ws, index) => {
            const wsError = (!ws.workstationId || ws.workstationId.trim() === '') ? 'Workstation ID is required' : undefined;
            return (
            <Box key={ws.id} type="outline" spacingInset="200">
              <Row alignmentHorizontal="justify" alignmentVertical="center" spacing="300">
                <Input
                  value={ws.workstationId}
                  onChange={(value: string) => handleRename(index, value)}
                  size="small"
                  label="Workstation name"
                  error={!!wsError}
                  errorMessage={wsError}
                />
                <Toggle
                  checked={ws.enabled}
                  onChange={(checked: boolean) => handleToggle(index, checked)}
                  size="small"
                />
                <Button type="icon" size="small" onClick={() => handleRemove(index)} label="Remove workstation">
                  <Icon tokens={trashTokens} />
                </Button>
              </Row>
            </Box>
            );
          })}
        </Column>
      )}

      {showInput ? (
        <Row spacing="200" alignmentVertical="bottom">
          <Input
            value={inputValue}
            onChange={setInputValue}
            onKeyDown={handleKeyDown}
            placeholder="e.g. WS-001"
            label="Workstation ID"
            size="small"
          />
          <Button type="primary" size="small" onClick={handleAdd}>
            Add
          </Button>
          <Button type="tertiary" size="small" onClick={() => { setShowInput(false); setInputValue(''); }}>
            Cancel
          </Button>
        </Row>
      ) : (
        <Row>
          <Button type="tertiary" size="small" onClick={() => setShowInput(true)}>
            + Add workstation
          </Button>
        </Row>
      )}

      {workstations.length === 0 && !showInput && (
        <Text type="b100" color="secondary">No workstations added</Text>
      )}
    </Column>
  );
};

export default WorkstationChipList;
